// @mui
import {
  Stack,
  Grid,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
// hooks
import { useCallback, useEffect, useMemo, useState } from 'react';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';
import Image from 'src/components/Image';
import TextMaxLine from 'src/components/TextMaxLine';
import ConfirmationDialog from '../../shared/ConfirmationDialog';
import useIsFullAdmin from 'src/hooks/useIsFullAdmin';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { fDate } from 'src/utils/formatTime';
import { APIS } from 'src/utils/serviceUrls';
import { SuccessStoryModal } from './SuccessStoryModal';
import { StoryTab, storyTypeOf, useSuccessStories } from '../useSuccessStories';
import { SuccessStory } from 'src/@types/successStory';
import useContentReorder from '../../content-order/useContentReorder';
import ReorderControls, { MoveLabels } from '../../content-order/ReorderControls';
import OrderErrorAlert from '../../content-order/OrderErrorAlert';
import { mergeScope, positionsBy } from '../../content-order/reorderModel';

type FilterType = StoryTab;

const STORY_MOVE_LABELS: MoveLabels = {
  up: 'Move earlier',
  down: 'Move later',
  top: 'Move to first',
  bottom: 'Move to last',
};

const ALL_TAB_HINT = 'Open Stories or Activities to change the order.';

// ----------------------------------------------------------------------

export default function SuccessStoriesList() {
  const {
    stories,
    setStories,
    modalOpen,
    setModalOpen,
    currentStory,
    setCurrentStory,
    getList,
    handleEditRow,
    handleDeleteRow,
  } = useSuccessStories();

  const [confirmOpen, setConfirmOpen] = useState({ open: false, currentId: '' });
  // DELETE is admin-only on the backend; create/edit are open to customer-care
  // and sales.
  const canDelete = useIsFullAdmin();
  // Stories first: the order of a type is changed on its own tab, never on "All".
  const [filter, setFilter] = useState<FilterType>('story');

  useEffect(() => {
    getList().catch(console.error);
  }, [getList]);

  const filteredStories = stories.filter((story: SuccessStory) => {
    if (filter === 'all') return true;
    return storyTypeOf(story) === filter;
  });

  // "#n" = the position inside the story's own type (the order the site uses).
  const positions = useMemo(() => positionsBy(stories, storyTypeOf), [stories]);

  // The answer is one type's stories in their new order: they go back into
  // that type's slots of the full list.
  const applyScope = useCallback(
    (scopeRows: SuccessStory[]) => {
      if (!scopeRows.length) return;
      const type = storyTypeOf(scopeRows[0]);
      setStories((all) => mergeScope(all, (r) => storyTypeOf(r) === type, scopeRows));
    },
    [setStories]
  );
  const reorder = useContentReorder<SuccessStory>({
    url: APIS.SUCCESS_STORIES.REORDER,
    apply: applyScope,
    reload: getList,
  });
  const canReorder = filter !== 'all';

  return (
    <Stack>
      <HeaderBreadcrumbs
        heading="Success Stories"
        links={[
          { name: 'Dashboard', href: PATH_DASHBOARD.root },
          { name: 'Success Stories', href: PATH_DASHBOARD.successStories },
        ]}
        action={
          <Button
            variant="contained"
            onClick={() => {
              setCurrentStory(undefined);
              setModalOpen(true);
            }}
            startIcon={<Iconify icon={'eva:plus-fill'} />}
          >
            New Story
          </Button>
        }
      />

      <Tabs
        value={filter}
        onChange={(_, value) => {
          reorder.clearError();
          setFilter(value);
        }}
        sx={{ mb: 1 }}
      >
        <Tab value="story" label="Stories" />
        <Tab value="activity" label="Activities" />
        <Tab value="all" label="All" />
      </Tabs>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
        Use the arrows to change the order. It is saved at once. On the home page, the Activities
        tab opens first when there are activities.
      </Typography>

      <OrderErrorAlert
        error={reorder.error}
        saving={reorder.saving}
        onClose={reorder.clearError}
        hint={canReorder ? null : ALL_TAB_HINT}
      />

      <Grid container spacing={2}>
        {filteredStories.map((story: SuccessStory, index: number) => (
          <Grid key={story._id} item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              {story.images && story.images.length > 0 && (
                <Image alt={story.name} src={story.images[0]} ratio="4/3" />
              )}
              <CardContent>
                <Stack
                  direction={'row'}
                  justifyContent={'space-between'}
                  alignItems={'flex-start'}
                  spacing={1}
                >
                  <Stack>
                    <Typography variant="subtitle1">{story.name}</Typography>
                    {story.title && (
                      <Typography variant="caption" color={'text.secondary'}>
                        {story.title}
                      </Typography>
                    )}
                    {(story.type || 'story') === 'activity' && story.date && (
                      <Typography variant="caption" color={'text.secondary'}>
                        {fDate(story.date)}
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction={'row'} spacing={0.5} alignItems={'flex-start'}>
                    <Chip
                      size="small"
                      label={(story.type || 'story') === 'activity' ? 'Activity' : 'Story'}
                      color={(story.type || 'story') === 'activity' ? 'info' : 'warning'}
                    />
                    <Chip
                      size="small"
                      label={story.active ? 'Active' : 'Inactive'}
                      color={story.active ? 'success' : 'default'}
                    />
                  </Stack>
                </Stack>

                <TextMaxLine line={2} variant={'body2'} sx={{ mt: 1, color: 'text.disabled' }}>
                  {story.story}
                </TextMaxLine>

                <Stack
                  direction={'row'}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                  mt={1}
                >
                  <Stack direction={'row'} spacing={0.5} alignItems={'center'}>
                    <Typography variant="caption" color={'text.secondary'} sx={{ minWidth: 28 }}>
                      #{positions.get(String(story._id)) ?? index + 1}
                    </Typography>
                    <ReorderControls
                      name={story.name || `story ${index + 1}`}
                      index={index}
                      count={filteredStories.length}
                      labels={STORY_MOVE_LABELS}
                      disabled={!canReorder || reorder.saving}
                      disabledHint={canReorder ? undefined : ALL_TAB_HINT}
                      onMove={(kind) =>
                        reorder.move(filteredStories, index, kind, { type: filter })
                      }
                    />
                  </Stack>
                  <Stack direction={'row'} spacing={0.5}>
                    <IconButton size="small" onClick={() => handleEditRow(story)}>
                      <Iconify icon={'eva:edit-fill'} />
                    </IconButton>
                    {canDelete && (
                      <IconButton
                        size="small"
                        sx={{ color: 'error.main' }}
                        onClick={() => setConfirmOpen({ open: true, currentId: story._id })}
                      >
                        <Iconify icon={'eva:trash-2-outline'} />
                      </IconButton>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {filteredStories.length === 0 && (
          <Stack width={'100%'} height="300px" alignItems={'center'} justifyContent="center">
            <Typography> No success stories yet</Typography>
          </Stack>
        )}
      </Grid>

      <SuccessStoryModal
        open={modalOpen}
        handleClose={() => {
          setModalOpen(false);
          getList();
          setCurrentStory(undefined);
        }}
        isEdit={currentStory ? true : false}
        currentStory={currentStory}
      />

      <ConfirmationDialog
        headText={'Delete Story'}
        text={'Are You Sure You Need To Delete This Story'}
        isOpen={confirmOpen.open}
        onClose={() => {
          setConfirmOpen({ open: false, currentId: '' });
          getList();
        }}
        confirmAction={() => {
          handleDeleteRow(confirmOpen.currentId).then(() => getList());
        }}
        loading={false}
      />
    </Stack>
  );
}
